import React from "react";
import { Asset, AssetRateCard, ProjectSetup, RateCard, Shot, TaskType } from "../types";
import { formatCurrency } from "../utils";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Download, FileText } from "lucide-react";
import { isBidItemIncluded } from "../lib/bidStatus";

interface Props {
  setup: ProjectSetup;
  rates: RateCard;
  assetRates: AssetRateCard;
  shots: Shot[];
  assets: Asset[];
  onExportPDF?: () => void;
}

export function ExportButtons({ setup, rates, assetRates, shots, assets, onExportPDF }: Props) {
  const calculateShotTotal = (shot: Shot) => {
    const baseTaskCost =
      (shot.days.concept || 0) * (rates.concept || 0) +
      (shot.days.dmp || 0) * (rates.dmp || 0) +
      (shot.days.comp || 0) * (rates.comp || 0) +
      (shot.days.roto || 0) * (rates.roto || 0) +
      (shot.days.prep || 0) * (rates.prep || 0) +
      (shot.days.gfx || 0) * (rates.gfx || 0) +
      (shot.days.cameraTR || 0) * (rates.cameraTR || 0) +
      (shot.days.objectTR || 0) * (rates.objectTR || 0) +
      (shot.days.rANIM || 0) * (rates.rANIM || 0) +
      (shot.days.layout || 0) * (rates.layout || 0) +
      (shot.days.anim || 0) * (rates.anim || 0) +
      (shot.days.light || 0) * (rates.light || 0) +
      (shot.days.fx || 0) * (rates.fx || 0);
    
    const taskDays = 
      (shot.days.concept || 0) +
      (shot.days.dmp || 0) +
      (shot.days.comp || 0) +
      (shot.days.roto || 0) +
      (shot.days.prep || 0) +
      (shot.days.gfx || 0) +
      (shot.days.cameraTR || 0) +
      (shot.days.objectTR || 0) +
      (shot.days.rANIM || 0) +
      (shot.days.layout || 0) +
      (shot.days.anim || 0) +
      (shot.days.light || 0) +
      (shot.days.fx || 0);

    const supDays = taskDays * ((setup.supervisionPercentage || 0) / 100);
    const supCost = supDays * (rates.supervisor || 0);
    const taskCost = baseTaskCost + supCost;
    return taskCost * (shot.quantity || 1);
  };

  const calculateAssetTotal = (asset: Asset) => {
    const baseTaskCost =
      (asset.days.concept || 0) * (assetRates.concept || 0) +
      (asset.days.dmp || 0) * (assetRates.dmp || 0) +
      (asset.days.model || 0) * (assetRates.model || 0) +
      (asset.days.rig || 0) * (assetRates.rig || 0) +
      (asset.days.retop || 0) * (assetRates.retop || 0) +
      (asset.days.cloth || 0) * (assetRates.cloth || 0) +
      (asset.days.fur || 0) * (assetRates.fur || 0) +
      (asset.days.groom || 0) * (assetRates.groom || 0) +
      (asset.days.texture || 0) * (assetRates.texture || 0) +
      (asset.days.anim || 0) * (assetRates.anim || 0) +
      (asset.days.fx || 0) * (assetRates.fx || 0) +
      (asset.days.lookdev || 0) * (assetRates.lookdev || 0) +
      (asset.days.photoscan || 0) * (assetRates.photoscan || 0);
    
    const taskDays = 
      (asset.days.concept || 0) +
      (asset.days.dmp || 0) +
      (asset.days.model || 0) +
      (asset.days.rig || 0) +
      (asset.days.retop || 0) +
      (asset.days.cloth || 0) +
      (asset.days.fur || 0) +
      (asset.days.groom || 0) +
      (asset.days.texture || 0) +
      (asset.days.anim || 0) +
      (asset.days.fx || 0) +
      (asset.days.lookdev || 0) +
      (asset.days.photoscan || 0);

    const supDays = taskDays * ((setup.supervisionPercentage || 0) / 100);
    const supCost = supDays * (assetRates.supervisor || 0);
    const taskCost = baseTaskCost + supCost;
    return taskCost * (asset.quantity || 1);
  };

  const calculateShotTotalDays = (shot: Shot) => {
    const otherDays =
      (shot.days.concept || 0) +
      (shot.days.dmp || 0) +
      (shot.days.comp || 0) +
      (shot.days.roto || 0) +
      (shot.days.prep || 0) +
      (shot.days.gfx || 0) +
      (shot.days.cameraTR || 0) +
      (shot.days.objectTR || 0) +
      (shot.days.rANIM || 0) +
      (shot.days.layout || 0) +
      (shot.days.anim || 0) +
      (shot.days.light || 0) +
      (shot.days.fx || 0);
    const supDays = otherDays * ((setup.supervisionPercentage || 0) / 100);
    return otherDays + supDays;
  };

  const calculateAssetTotalDays = (asset: Asset) => {
    const otherDays =
      (asset.days.concept || 0) +
      (asset.days.dmp || 0) +
      (asset.days.model || 0) +
      (asset.days.rig || 0) +
      (asset.days.retop || 0) +
      (asset.days.cloth || 0) +
      (asset.days.fur || 0) +
      (asset.days.groom || 0) +
      (asset.days.texture || 0) +
      (asset.days.anim || 0) +
      (asset.days.fx || 0) +
      (asset.days.lookdev || 0) +
      (asset.days.photoscan || 0);
    const supDays = otherDays * ((setup.supervisionPercentage || 0) / 100);
    return otherDays + supDays;
  };

  const exportPDF = async () => {
    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    // Column widths from spec (total 1195)
    const specWidths = [60, 50, 172, 150, 250, 59, 45, 250, 100, 59];
    const totalSpecWidth = specWidths.reduce((a, b) => a + b, 0);
    const scale = pageWidth / totalSpecWidth;
    const colWidths = specWidths.map(w => w * scale);

    const activeShots = shots.filter(s => isBidItemIncluded(s));
    const activeAssets = assets.filter(a => isBidItemIncluded(a));
    const shotTotal = activeShots.reduce((acc, shot) => acc + calculateShotTotal(shot), 0);
    const assetTotal = activeAssets.reduce((acc, asset) => acc + calculateAssetTotal(asset), 0);
    const onsetSupervisionCost = (setup.onsetSupervision || 0) * (rates.onsetSupervision ?? 600);
    const totalCost = shotTotal + assetTotal + onsetSupervisionCost;

    // Load logo
    const logoImg = new Image();
    logoImg.src = '/logo.png';
    await new Promise((resolve) => {
      logoImg.onload = resolve;
      logoImg.onerror = resolve;
    });

    const drawFooter = () => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6);
      doc.setTextColor(0, 0, 0);
      const footerText = "Vine FX Ltd is registered in England and Wales • Registered Number: 09194543 • VAT Registered Number: GB264100542";
      doc.text(footerText, pageWidth / 2, pageHeight - 20, { align: "center" });
    };

    const drawYellowBar = (y: number = 0) => {
      doc.setFillColor(255, 204, 1); // #FFCC01
      doc.rect(0, y, pageWidth, 21 * scale, "F");
    };

    // Construct the spreadsheet grid
    const shotTableRows: any[] = [];
    const assetTableRows: any[] = [];

    // Row 2: 110 high row
    const row2 = Array.from({ length: 10 }, () => ({ content: "", styles: { minCellHeight: 110 * scale } }));
    
    // Summary rows (Height 30 each)
    const summaryLabels = [
      "Estimate Name",
      "Estimate Date",
      "Shot Total",
      "Asset Total",
      "Shot Count",
      "Total"
    ];
    const summaryValues = [
      setup.projectName,
      new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
      formatCurrency(shotTotal, setup.currency),
      formatCurrency(assetTotal, setup.currency),
      activeShots.length.toString(),
      formatCurrency(totalCost, setup.currency)
    ];

    const summaryRows: any[][] = [];
    summaryLabels.forEach((label, i) => {
      const commonStyles = { 
        fontSize: 6,
        valign: 'middle' as const,
        cellPadding: 2
      };
      
      const grayStyles = {
        ...commonStyles,
        fillColor: [243, 243, 243] as [number, number, number]
      };

      const whiteStyles = {
        ...commonStyles,
        fillColor: [255, 255, 255] as [number, number, number]
      };

      let row: any[] = [];

      // Column A (0)
      row.push({ content: "", styles: whiteStyles });
      // Column B (1)
      row.push({ content: "", styles: whiteStyles });
      // Column C (2): Label
      row.push({ 
        content: label, 
        styles: { 
          ...grayStyles,
          fontStyle: 'bold' as const, 
          halign: 'left' as const
        } 
      });
      // Column D (3): Value
      row.push({ 
        content: summaryValues[i], 
        styles: { 
          ...whiteStyles,
          halign: 'left' as const
        } 
      });
      // Column E (4)
      row.push({ content: "", styles: whiteStyles });
      // Column F (5)
      row.push({ content: "", styles: whiteStyles });
      // Column G (6)
      row.push({ content: "", styles: whiteStyles });
      // Column H (7)
      if (i === 0) {
        row.push({ 
          content: "", 
          rowSpan: 3, 
          colSpan: 2, 
          styles: whiteStyles 
        });
      } else if (i === 3 || i === 5) {
        row.push({ content: "", styles: whiteStyles });
      } else if (i === 4) {
        row.push({ 
          content: "  Bateman House • 82-88 Hills Road • Cambridge • CB2 1LQ\n✪ www.vinefx.com ☏ +44 1223 734990", 
          colSpan: 2, 
          styles: {
            ...whiteStyles,
            halign: 'right' as const,
            fontSize: 6
          } 
        });
      }

      // Column I (8)
      if (i === 3 || i === 5) {
        row.push({ content: "", styles: whiteStyles });
      }

      // Column J (9)
      row.push({ content: "", styles: whiteStyles });

      summaryRows.push(row);
    });

    // Row 9: White spacer (Height 8)
    const spacerRow = [
      { content: "", styles: { minCellHeight: 8 * scale } }, // A
      { content: "", styles: { minCellHeight: 8 * scale } }, // B
      { content: "", styles: { minCellHeight: 8 * scale } }, // C
      { content: "", styles: { minCellHeight: 8 * scale } }, // D
      { content: "", styles: { minCellHeight: 8 * scale } }, // E
      { content: "", styles: { minCellHeight: 8 * scale } }, // F
      { content: "", styles: { minCellHeight: 8 * scale } }, // G
      { content: "", styles: { minCellHeight: 8 * scale } }, // H
      { content: "", styles: { minCellHeight: 8 * scale } }, // I
      { content: "", styles: { minCellHeight: 8 * scale } }  // J
    ];

    // Table header (Height 30)
    const headerRow = [
      "", // A
      { content: "#", styles: { fontStyle: 'bold' as const, fontSize: 6, fillColor: [255, 204, 1] as [number, number, number] } }, // B
      { content: "Thumbnail", styles: { fontStyle: 'bold' as const, fontSize: 6, fillColor: [255, 204, 1] as [number, number, number] } }, // C
      { content: "Shot Number", styles: { fontStyle: 'bold' as const, fontSize: 6, halign: 'center' as const, fillColor: [255, 204, 1] as [number, number, number] } }, // D
      { content: "Task Description", styles: { fontStyle: 'bold' as const, fontSize: 6, fillColor: [255, 204, 1] as [number, number, number] } }, // E
      { content: "Frames", styles: { fontStyle: 'bold' as const, fontSize: 6, halign: 'center' as const, fillColor: [255, 204, 1] as [number, number, number] } }, // F
      { content: "Qty", styles: { fontStyle: 'bold' as const, fontSize: 6, halign: 'center' as const, fillColor: [255, 204, 1] as [number, number, number] } }, // G
      { content: "Assumptions", styles: { fontStyle: 'bold' as const, fontSize: 6, fillColor: [255, 204, 1] as [number, number, number] } }, // H
      { content: "Price", styles: { fontStyle: 'bold' as const, fontSize: 6, halign: 'center' as const, fillColor: [255, 204, 1] as [number, number, number] } }, // I
      "" // J
    ];

    // Header for Assets (same layout, different label)
    const assetHeaderRow = [...headerRow];
    assetHeaderRow[3] = { content: "Asset Code", styles: { fontStyle: 'bold' as const, fontSize: 6, halign: 'center' as const, fillColor: [255, 204, 1] as [number, number, number] } };

    // Data rows
    const shotItems: any[] = [];
    if (setup.onsetSupervision > 0) {
      shotItems.push({
        index: "1",
        thumbnail: null,
        shot_number: "Onset Supervision",
        task_description: "ONSET VFX SUPERVISION",
        frames: "",
        qty: setup.onsetSupervision.toString(),
        assumptions: "",
        price: formatCurrency(onsetSupervisionCost, setup.currency)
      });
    }

    activeShots.forEach((shot) => {
      shotItems.push({
        index: (shotItems.length + 1).toString(),
        thumbnail: shot.thumbnail,
        shot_number: shot.shotCode,
        task_description: shot.description,
        frames: (shot.frames || 0).toString(),
        qty: shot.quantity.toString(),
        assumptions: shot.assumptions,
        price: formatCurrency(calculateShotTotal(shot), setup.currency)
      });
    });

    const assetItems: any[] = [];
    activeAssets.forEach((asset) => {
      assetItems.push({
        index: (assetItems.length + 1).toString(),
        thumbnail: asset.thumbnail,
        shot_number: asset.assetCode,
        task_description: asset.description,
        frames: "",
        qty: asset.quantity.toString(),
        assumptions: asset.assumptions,
        price: formatCurrency(calculateAssetTotal(asset), setup.currency)
      });
    });

    // Build Shot Table Body
    shotTableRows.push(row2);
    summaryRows.forEach(r => shotTableRows.push(r));
    shotTableRows.push(spacerRow);
    shotTableRows.push(headerRow);
    shotItems.forEach(item => {
      shotTableRows.push([
        "", // A
        item.index,
        { content: "", styles: { fillColor: [243, 243, 243] as [number, number, number], minCellHeight: 99 * scale } }, // C: Thumbnail (shaded)
        { content: item.shot_number, styles: { halign: 'center' as const } }, // D: Shot Number (centered)
        item.task_description,
        { content: item.frames, styles: { halign: 'center' as const, fillColor: [243, 243, 243] as [number, number, number] } }, // F: Frames (centered, gray)
        { content: item.qty, styles: { halign: 'center' as const, fillColor: [243, 243, 243] as [number, number, number] } }, // G: Qty (centered, gray)
        item.assumptions,
        { content: item.price, styles: { halign: 'center' as const, noWrap: true } }, // I: Price (centered)
        "" // J
      ]);
    });

    // Build Asset Table Body
    assetTableRows.push(row2);
    summaryRows.forEach(r => assetTableRows.push(r));
    assetTableRows.push(spacerRow);
    assetTableRows.push(assetHeaderRow);
    assetItems.forEach(item => {
      assetTableRows.push([
        "", // A
        item.index,
        { content: "", styles: { fillColor: [243, 243, 243] as [number, number, number], minCellHeight: 99 * scale } }, // C: Thumbnail (shaded)
        { content: item.shot_number, styles: { halign: 'center' as const } }, // D: Shot Number (centered)
        item.task_description,
        { content: item.frames, styles: { halign: 'center' as const, fillColor: [243, 243, 243] as [number, number, number] } }, // F: Frames (centered, gray)
        { content: item.qty, styles: { halign: 'center' as const, fillColor: [243, 243, 243] as [number, number, number] } }, // G: Qty (centered, gray)
        item.assumptions,
        { content: item.price, styles: { halign: 'center' as const, noWrap: true } }, // I: Price (centered)
        "" // J
      ]);
    });

    const commonTableStyles = {
      fontSize: 6,
      cellPadding: 2,
      valign: 'middle' as const,
      font: "helvetica",
      lineWidth: 0.1,
      lineColor: [255, 204, 1] as [number, number, number], // Orange/Yellow grid lines
      overflow: 'linebreak' as const,
      minCellHeight: 30 * scale
    };

    const commonColumnStyles = {
      0: { cellWidth: colWidths[0] }, // A
      1: { cellWidth: colWidths[1], halign: 'center' as const }, // B
      2: { cellWidth: colWidths[2] }, // C
      3: { cellWidth: colWidths[3] }, // D
      4: { cellWidth: colWidths[4] }, // E
      5: { cellWidth: colWidths[5] }, // F
      6: { cellWidth: colWidths[6] }, // G
      7: { cellWidth: colWidths[7] }, // H
      8: { cellWidth: colWidths[8] }, // I
      9: { cellWidth: colWidths[9] }  // J
    };

    // Draw Shot Table
    autoTable(doc, {
      startY: 21 * scale,
      margin: { left: 0, right: 0, top: (21 + 110 + 30) * scale, bottom: 40 },
      body: shotTableRows,
      theme: 'grid',
      rowPageBreak: 'avoid',
      styles: commonTableStyles,
      columnStyles: commonColumnStyles,
      didDrawPage: (data) => {
        drawYellowBar(0);
        drawFooter();
        
        // Repeat Row 2 and Header on new pages
        if (data.pageNumber > 1) {
          // Draw Row 2
          autoTable(doc, {
            startY: 21 * scale,
            margin: { left: 0, right: 0 },
            body: [row2],
            theme: 'grid',
            styles: {
              lineWidth: 0.1,
              lineColor: [255, 204, 1],
              minCellHeight: 110 * scale
            },
            columnStyles: commonColumnStyles
          });

          // Draw Header Row
          autoTable(doc, {
            startY: (21 + 110) * scale,
            margin: { left: 0, right: 0 },
            body: [headerRow],
            theme: 'grid',
            styles: {
              fontSize: 6,
              fontStyle: 'bold' as const,
              valign: 'middle',
              cellPadding: 2,
              font: "helvetica",
              lineWidth: 0.1,
              lineColor: [255, 204, 1],
              minCellHeight: 30 * scale
            },
            columnStyles: commonColumnStyles
          });
        }
      },
      didDrawCell: (data) => {
        // Logo placement in H3 (merged H3:I5)
        if (data.row.index === 1 && data.column.index === 7 && logoImg.complete && logoImg.naturalHeight !== 0) {
          const padding = 0;
          const cellW = data.cell.width;
          const cellH = data.cell.height;
          const imgRatio = logoImg.width / logoImg.height;
          
          let drawH = cellH;
          let drawW = cellH * imgRatio;
          
          if (drawW > cellW) {
            drawW = cellW;
            drawH = cellW / imgRatio;
          }
          
          doc.addImage(
            logoImg, 
            'PNG', 
            data.cell.x + data.cell.width - drawW, 
            data.cell.y + (cellH - drawH) / 2, 
            drawW, 
            drawH
          );
        }

        // Thumbnail placement in Data Rows
        const dataRowOffset = 9; // Row 2 (1) + Summary (6) + Spacer (1) + Header (1)
        if (data.row.index >= dataRowOffset && data.column.index === 2) {
          const item = shotItems[data.row.index - dataRowOffset];
          if (item && item.thumbnail) {
            try {
              const padding = 0.5;
              const cellW = data.cell.width - (padding * 2);
              const cellH = data.cell.height - (padding * 2);
              const targetRatio = 16 / 9;
              
              let drawW = cellW;
              let drawH = cellW / targetRatio;
              if (drawH > cellH) {
                drawH = cellH;
                drawW = cellH * targetRatio;
              }

              doc.addImage(
                item.thumbnail, 
                'JPEG', 
                data.cell.x + padding + (cellW - drawW) / 2, 
                data.cell.y + padding + (cellH - drawH) / 2, 
                drawW, 
                drawH
              );
            } catch (e) {
              // ignore
            }
          }
        }
      }
    });

    // Draw Asset Table on new page if exists
    if (activeAssets.length > 0) {
      doc.addPage();
      
      autoTable(doc, {
        startY: 21 * scale,
        margin: { left: 0, right: 0, top: (21 + 110 + 30) * scale, bottom: 40 },
        body: assetTableRows,
        theme: 'grid',
        rowPageBreak: 'avoid',
        styles: commonTableStyles,
        columnStyles: commonColumnStyles,
        didDrawPage: (data) => {
          drawYellowBar(0);
          drawFooter();
          
          // Repeat Row 2 and Header on new pages
          if (data.pageNumber > 1) {
            // Draw Row 2
            autoTable(doc, {
              startY: 21 * scale,
              margin: { left: 0, right: 0 },
              body: [row2],
              theme: 'grid',
              styles: {
                lineWidth: 0.1,
                lineColor: [255, 204, 1],
                minCellHeight: 110 * scale
              },
              columnStyles: commonColumnStyles
            });

            // Draw Header Row
            autoTable(doc, {
              startY: (21 + 110) * scale,
              margin: { left: 0, right: 0 },
              body: [assetHeaderRow],
              theme: 'grid',
              styles: {
                fontSize: 6,
                fontStyle: 'bold' as const,
                valign: 'middle',
                cellPadding: 2,
                font: "helvetica",
                lineWidth: 0.1,
                lineColor: [255, 204, 1],
                minCellHeight: 30 * scale
              },
              columnStyles: commonColumnStyles
            });
          }
        },
        didDrawCell: (data) => {
          // Logo placement in H3 (merged H3:I5)
          if (data.row.index === 1 && data.column.index === 7 && logoImg.complete && logoImg.naturalHeight !== 0) {
            const padding = 0;
            const cellW = data.cell.width;
            const cellH = data.cell.height;
            const imgRatio = logoImg.width / logoImg.height;
            
            let drawH = cellH;
            let drawW = cellH * imgRatio;
            
            if (drawW > cellW) {
              drawW = cellW;
              drawH = cellW / imgRatio;
            }
            
            doc.addImage(
              logoImg, 
              'PNG', 
              data.cell.x + data.cell.width - drawW, 
              data.cell.y + (cellH - drawH) / 2, 
              drawW, 
              drawH
            );
          }

          // Thumbnail placement in Data Rows
          const dataRowOffset = 9; // Row 2 (1) + Summary (6) + Spacer (1) + Header (1)
          if (data.row.index >= dataRowOffset && data.column.index === 2) {
            const item = assetItems[data.row.index - dataRowOffset];
            if (item && item.thumbnail) {
              try {
                const padding = 0.5;
                const cellW = data.cell.width - (padding * 2);
                const cellH = data.cell.height - (padding * 2);
                const targetRatio = 16 / 9;
                
                let drawW = cellW;
                let drawH = cellW / targetRatio;
                if (drawH > cellH) {
                  drawH = cellH;
                  drawW = cellH * targetRatio;
                }

                doc.addImage(
                  item.thumbnail, 
                  'JPEG', 
                  data.cell.x + padding + (cellW - drawW) / 2, 
                  data.cell.y + padding + (cellH - drawH) / 2, 
                  drawW, 
                  drawH
                );
              } catch (e) {
                // ignore
              }
            }
          }
        }
      });
    }

    doc.save(`${setup.projectName.replace(/\s+/g, '_')}_Estimate.pdf`);
    if (onExportPDF) {
      onExportPDF();
    }
  };

  const exportAssetsCSV = () => {
    const hasTargetCurrency = setup.targetCurrency && setup.exchangeRate;
    const headers = [
      "Active",
      "Asset Code",
      "Description",
      "Quantity",
      "Supervision Days",
      "Concept Days",
      "DMP Days",
      "Model Days",
      "Rig Days",
      "Retop Days",
      "Cloth Days",
      "Fur Days",
      "Groom Days",
      "Texture Days",
      "Anim Days",
      "FX Days",
      "Lookdev Days",
      "Photoscan Days",
      `Total Cost (${setup.currency})`,
      "Assumptions",
      "Internal Notes"
    ];

    if (hasTargetCurrency) {
      headers.push(`Total Cost (${setup.targetCurrency})`);
    }

    const rows = assets.map(asset => {
      const assetTotal = calculateAssetTotal(asset);
      const otherDays =
        (asset.days.concept || 0) +
        (asset.days.dmp || 0) +
        (asset.days.model || 0) +
        (asset.days.rig || 0) +
        (asset.days.retop || 0) +
        (asset.days.cloth || 0) +
        (asset.days.fur || 0) +
        (asset.days.groom || 0) +
        (asset.days.texture || 0) +
        (asset.days.anim || 0) +
        (asset.days.fx || 0) +
        (asset.days.lookdev || 0) +
        (asset.days.photoscan || 0);
      const supDays = otherDays * ((assetRates.supervisor || 0) / 100);

      const row = [
        asset.active ? "Yes" : "No",
        `"${asset.assetCode}"`,
        `"${asset.description.replace(/"/g, '""')}"`,
        asset.quantity || 1,
        supDays.toFixed(2),
        asset.days.concept || 0,
        asset.days.dmp || 0,
        asset.days.model || 0,
        asset.days.rig || 0,
        asset.days.retop || 0,
        asset.days.cloth || 0,
        asset.days.fur || 0,
        asset.days.groom || 0,
        asset.days.texture || 0,
        asset.days.anim || 0,
        asset.days.fx || 0,
        asset.days.lookdev || 0,
        asset.days.photoscan || 0,
        assetTotal,
        `"${asset.assumptions.replace(/"/g, '""')}"`,
        `"${asset.internalNotes.replace(/"/g, '""')}"`
      ];
      if (hasTargetCurrency) {
        row.push(assetTotal * (setup.exchangeRate || 1));
      }
      return row;
    });

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${setup.projectName.replace(/\s+/g, '_')}_Assets.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportCSV = () => {
    const hasTargetCurrency = setup.targetCurrency && setup.exchangeRate;
    const headers = [
      "Active",
      "Shot Code",
      "Description",
      "Working Duration",
      "Quantity",
      "Supervision Days",
      "Concept Days",
      "DMP Days",
      "Comp Days",
      "Roto Days",
      "Prep Days",
      "GFX Days",
      "CameraTR Days",
      "ObjectTR Days",
      "rANIM Days",
      "Layout Days",
      "Anim Days",
      "Light Days",
      "FX Days",
      `Total Cost (${setup.currency})`,
      "Assumptions",
      "Internal Notes"
    ];

    if (hasTargetCurrency) {
      headers.push(`Total Cost (${setup.targetCurrency})`);
    }

    const rows = shots.map(shot => {
      const shotTotal = calculateShotTotal(shot);
      const otherDays =
        (shot.days.concept || 0) +
        (shot.days.dmp || 0) +
        (shot.days.comp || 0) +
        (shot.days.roto || 0) +
        (shot.days.prep || 0) +
        (shot.days.gfx || 0) +
        (shot.days.cameraTR || 0) +
        (shot.days.objectTR || 0) +
        (shot.days.rANIM || 0) +
        (shot.days.layout || 0) +
        (shot.days.anim || 0) +
        (shot.days.light || 0) +
        (shot.days.fx || 0);
      const supDays = otherDays * ((rates.supervisor || 0) / 100);

      const row = [
        shot.active ? "Yes" : "No",
        `"${shot.shotCode}"`,
        `"${shot.description.replace(/"/g, '""')}"`,
        shot.frames || 0,
        shot.quantity || 1,
        supDays.toFixed(2),
        shot.days.concept || 0,
        shot.days.dmp || 0,
        shot.days.comp || 0,
        shot.days.roto || 0,
        shot.days.prep || 0,
        shot.days.gfx || 0,
        shot.days.cameraTR || 0,
        shot.days.objectTR || 0,
        shot.days.rANIM || 0,
        shot.days.layout || 0,
        shot.days.anim || 0,
        shot.days.light || 0,
        shot.days.fx || 0,
        shotTotal,
        `"${shot.assumptions.replace(/"/g, '""')}"`,
        `"${shot.internalNotes.replace(/"/g, '""')}"`
      ];
      if (hasTargetCurrency) {
        row.push(shotTotal * (setup.exchangeRate || 1));
      }
      return row;
    });

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${setup.projectName.replace(/\s+/g, '_')}_Shots.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <button
          onClick={exportCSV}
          className="flex items-center justify-between gap-1.5 px-4 py-2 bg-white border border-slate-300 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
          title="Export Shots CSV"
        >
          <div className="flex items-center gap-2">
            <Download className="w-4 h-4 text-slate-400" />
            <span>Shots CSV</span>
          </div>
          <span className="text-[10px] text-slate-400 font-mono uppercase">.csv</span>
        </button>
        <button
          onClick={exportAssetsCSV}
          className="flex items-center justify-between gap-1.5 px-4 py-2 bg-white border border-slate-300 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
          title="Export Assets CSV"
        >
          <div className="flex items-center gap-2">
            <Download className="w-4 h-4 text-slate-400" />
            <span>Assets CSV</span>
          </div>
          <span className="text-[10px] text-slate-400 font-mono uppercase">.csv</span>
        </button>
      </div>
      <button
        onClick={exportPDF}
        className="flex items-center justify-between gap-1.5 px-4 py-2 bg-white border border-slate-300 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
      >
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-400" />
          <span>Export PDF</span>
        </div>
        <span className="text-[10px] text-slate-400 font-mono uppercase">.pdf</span>
      </button>
    </div>
  );
}
